import React, { FC, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames/bind';
import Text from '../../atoms/Text';
import Icon from '../../atoms/Icon';
import TierBadge from '../../atoms/TierBadge';
import { RONE_POINTS_ICON, SUPER_CASH_ICON } from '../../constants/centralLoyalty.constants';
import { segmentEventPush } from '../../../modules/ga/GTMdatalayer';
import styles from './index.pcss';

const cx = classNames.bind(styles);

export interface LoyaltyEarnWidgetProps {
  header: string;
  /** Tier badge is shown on Cart/SPC only; TY/ODP pass no tier. */
  tier?: string;
  tierLabel?: string;
  /** Flag-configured tier badge artwork. Always the dark set on this widget. */
  tierBadge?: string;
  /** Flag-configured RelianceOne mark (blue on AJIO/LUXE, white on AJIOGRAM). */
  logo?: string;
  ronePoints?: number;
  roneAmount?: number;
  roneLabel?: string;
  roneIcon?: string;
  superCashAmount?: number;
  superCashLabel?: string;
  superCashIcon?: string;
  disclaimer?: string;
  /** Accordion starts collapsed; the desktop variant ignores this and is always open. */
  defaultOpen?: boolean;
  /**
   * While the earn amounts are still being fetched, render a shimmer skeleton
   * instead of the real card. Callers whose data is already available (TY/ODP)
   * simply omit this and get today's behaviour.
   */
  loading?: boolean;
  /**
   * 'default' — msite: horizontal currency columns in a collapsible accordion.
   * 'desktop' — bordered static card, currencies stacked vertically, no chevron.
   */
  variant?: 'default' | 'desktop';
  /** Rendered inside the accordion, below the earn columns (e.g. cart coupon row). */
  children?: React.ReactNode;
  onToggle?: (isOpen: boolean) => void;
}

const formatRupee = (value: number): string => `₹${Number(value || 0).toFixed(0)}`;

/**
 * Shared RelianceOne + Supercash earn accordion.
 * Used on Cart/SPC (with tier badge) and TY/ODP (without).
 * Renders nothing when neither currency has a non-zero earn.
 */
const LoyaltyEarnWidget: FC<LoyaltyEarnWidgetProps> = (props: LoyaltyEarnWidgetProps) => {
  const {
    header = '',
    tier = '',
    tierLabel = '',
    tierBadge = '',
    logo = '',
    ronePoints = 0,
    roneAmount = 0,
    roneLabel = '',
    roneIcon = RONE_POINTS_ICON,
    superCashAmount = 0,
    superCashLabel = '',
    superCashIcon = SUPER_CASH_ICON,
    disclaimer = '',
    defaultOpen = false,
    variant = 'default',
    loading = false,
    children,
    onToggle
  } = props;

  const isDesktop = variant === 'desktop';
  const [isOpen, setIsOpen] = useState(defaultOpen);
  // Desktop is a static card: always expanded, no chevron, not clickable.
  const expanded = isDesktop || isOpen;

  const showRone = ronePoints > 0 || roneAmount > 0;
  const showSuperCash = superCashAmount > 0;

  const { ref: earningsSectionRef, inView: isEarningsSectionInView } = useInView({
    threshold: 0.5,
    triggerOnce: true
  });

  useEffect(() => {
    if (isEarningsSectionInView) {
      segmentEventPush({
        event: 'Earnings Section Viewed',
        page_url: window.location.href
      });
    }
  }, [isEarningsSectionInView]);

  // Earn amounts still in flight -> skeleton in place of the card. Checked
  // before the "nothing earned" guard below, since amounts are 0 while loading.
  if (loading) {
    // msite: a single 44px row — leading circle, a bar, and a trailing circle
    // pinned to the far end.
    if (!isDesktop) {
      return (
        <div
          className={cx('loyalty-earn-widget', 'loyalty-earn-widget--loading')}
          aria-busy="true"
          aria-hidden="true"
        >
          <div className={cx('loyalty-earn-widget__shimmer-row')}>
            <span className={cx('shimmer', 'loyalty-earn-widget__shimmer-circle')} />
            <span className={cx('shimmer', 'loyalty-earn-widget__shimmer-bar')} />
            <span
              className={cx(
                'shimmer',
                'loyalty-earn-widget__shimmer-circle',
                'loyalty-earn-widget__shimmer-circle--end'
              )}
            />
          </div>
        </div>
      );
    }

    return (
      <div
        className={cx(
          'loyalty-earn-widget',
          'loyalty-earn-widget--loading',
          'loyalty-earn-widget--desktop'
        )}
        aria-busy="true"
        aria-hidden="true"
      >
        <div className={cx('loyalty-earn-widget__header')}>
          <span className={cx('shimmer', 'loyalty-earn-widget__shimmer-logo')} />
          <span className={cx('shimmer', 'loyalty-earn-widget__shimmer-title')} />
          <span className={cx('shimmer', 'loyalty-earn-widget__shimmer-badge')} />
        </div>
        <div className={cx('loyalty-earn-widget__body')}>
          <div className={cx('loyalty-earn-widget__currencies')}>
            {[0, 1].map(i => (
              <div key={i} className={cx('loyalty-earn-widget__currency')}>
                <span className={cx('shimmer', 'loyalty-earn-widget__shimmer-icon')} />
                <div className={cx('loyalty-earn-widget__currency-content')}>
                  <span className={cx('shimmer', 'loyalty-earn-widget__shimmer-line')} />
                  <span
                    className={cx(
                      'shimmer',
                      'loyalty-earn-widget__shimmer-line',
                      'loyalty-earn-widget__shimmer-line--sm'
                    )}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Nothing earned on either currency -> no widget at all.
  if (!showRone && !showSuperCash) return null;

  const handleToggle = (): void => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    segmentEventPush({
      event: 'Earnings Section Clicked',
      page_url: window.location.href,
      reliance_one_points_earned: ronePoints,
      supercash_earned: superCashAmount
    });
    if (onToggle) onToggle(nextOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <div
      ref={earningsSectionRef}
      className={cx('loyalty-earn-widget', { 'loyalty-earn-widget--desktop': isDesktop })}
    >
      <div
        className={cx('loyalty-earn-widget__header')}
        role={isDesktop ? undefined : 'button'}
        tabIndex={isDesktop ? undefined : 0}
        aria-expanded={isDesktop ? undefined : isOpen}
        aria-label={`${header} ${tierLabel || tier}`.trim()}
        onClick={isDesktop ? undefined : handleToggle}
        onKeyDown={isDesktop ? undefined : handleKeyDown}
      >
        {logo && (
          <img className={cx('loyalty-earn-widget__logo')} src={logo} alt="" aria-hidden="true" />
        )}
        <Text
          variant="T2"
          color="Black"
          weight="bold"
          customClass={cx('loyalty-earn-widget__title')}
        >
          {header}
        </Text>
        {tier && <TierBadge tier={tier} label={tierLabel} badge={tierBadge} />}
        {!isDesktop && (
          <Icon
            icon={isOpen ? 'icon-Chevron-Up' : 'icon-Chevron-Down'}
            iconClass={cx('loyalty-earn-widget__chevron')}
            ariaHidden
          />
        )}
      </div>

      {expanded && (
        <div className={cx('loyalty-earn-widget__body')}>
          <div className={cx('loyalty-earn-widget__currencies')}>
            {showRone && (
              <div className={cx('loyalty-earn-widget__currency')}>
                {roneIcon && (
                  <img
                    className={cx('loyalty-earn-widget__currency-icon')}
                    src={roneIcon}
                    alt=""
                    aria-hidden="true"
                  />
                )}
                <div className={cx('loyalty-earn-widget__currency-content')}>
                  <Text variant="T2" color="Chinese_Black" weight="bold">
                    {roneLabel}
                  </Text>
                  <Text
                    variant="T2"
                    color="Dark_Grey"
                    customClass={cx('loyalty-earn-widget__currency-value')}
                    ariaLabel={`${ronePoints} points, ${formatRupee(roneAmount)}`}
                  >
                    {`${ronePoints} (${formatRupee(roneAmount)})`}
                  </Text>
                </div>
              </div>
            )}

            {showSuperCash && (
              <div className={cx('loyalty-earn-widget__currency')}>
                {superCashIcon && (
                  <img
                    className={cx('loyalty-earn-widget__currency-icon')}
                    src={superCashIcon}
                    alt=""
                    aria-hidden="true"
                  />
                )}
                <div className={cx('loyalty-earn-widget__currency-content')}>
                  <Text variant="T2" color="Chinese_Black" weight="bold">
                    {superCashLabel}
                  </Text>
                  <Text
                    variant="T2"
                    color="Dark_Grey"
                    customClass={cx('loyalty-earn-widget__currency-value')}
                  >
                    {formatRupee(superCashAmount)}
                  </Text>
                </div>
              </div>
            )}
          </div>

          {disclaimer && (
            <Text
              variant="T1"
              color="Dark_Grey"
              customClass={cx('loyalty-earn-widget__disclaimer')}
            >
              {disclaimer}
            </Text>
          )}

          {children}
        </div>
      )}
    </div>
  );
};

export default LoyaltyEarnWidget;
